import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { GET_MY_SCHEDULE } from '../../graphql/my-availability-queries';
import { SET_MY_WEEKLY_SCHEDULE } from '../../graphql/my-availability-mutations';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const buildBlankSchedule = () =>
  [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
    dayOfWeek,
    isWorking: null,
    isRemote: false,
  }));

const mergeWithBlank = (existingPattern) => {
  if (!Array.isArray(existingPattern) || existingPattern.length === 0) {
    return buildBlankSchedule();
  }
  return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
    const match = existingPattern.find((entry) => entry.dayOfWeek === dayOfWeek);
    if (!match) return { dayOfWeek, isWorking: false, isRemote: false };
    return {
      dayOfWeek,
      isWorking: match.isWorking,
      isRemote: match.isRemote === true,
    };
  });
};

function MyScheduleCard({ onSaved }) {
  const [schedule, setSchedule] = useState(buildBlankSchedule);
  const [isDirty, setIsDirty] = useState(false);

  const { data, loading, refetch } = useQuery(GET_MY_SCHEDULE, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.mySchedule) {
      setSchedule(mergeWithBlank(data.mySchedule));
      setIsDirty(false);
    }
  }, [data]);

  const [saveSchedule, { loading: saving }] = useMutation(SET_MY_WEEKLY_SCHEDULE, {
    onCompleted: () => {
      toast.success('Schedule saved');
      setIsDirty(false);
      refetch();
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save schedule');
    },
  });

  const workingCount = useMemo(
    () => schedule.filter((entry) => entry.isWorking === true).length,
    [schedule]
  );
  const remoteCount = useMemo(
    () => schedule.filter((entry) => entry.isWorking === true && entry.isRemote === true).length,
    [schedule]
  );

  const handleToggleStatus = (dayOfWeek) => {
    setSchedule((prev) =>
      prev.map((entry) => {
        if (entry.dayOfWeek !== dayOfWeek) return entry;
        if (entry.isWorking === null) return { ...entry, isWorking: true, isRemote: false };
        if (entry.isWorking === true) return { ...entry, isWorking: false, isRemote: false };
        return { ...entry, isWorking: true, isRemote: false };
      })
    );
    setIsDirty(true);
  };

  const handleLocationChange = (dayOfWeek, nextIsRemote) => {
    setSchedule((prev) =>
      prev.map((entry) => {
        if (entry.dayOfWeek !== dayOfWeek) return entry;
        if (entry.isWorking !== true) return entry;
        if (entry.isRemote === nextIsRemote) return entry;
        return { ...entry, isRemote: nextIsRemote };
      })
    );
    setIsDirty(true);
  };

  const handleSave = () => {
    const explicit = schedule.filter((entry) => entry.isWorking !== null);
    if (explicit.length === 0) {
      toast.error('Please mark at least one day as working or off');
      return;
    }
    const days = [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
      const match = explicit.find((entry) => entry.dayOfWeek === dayOfWeek);
      const isWorking = match ? match.isWorking === true : false;
      return {
        dayOfWeek,
        isWorking,
        isRemote: isWorking && match?.isRemote === true,
      };
    });
    saveSchedule({ variables: { input: { days } } });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
          <h2 className="text-lg font-semibold text-gray-900">My Weekly Schedule</h2>
          <span className="text-sm text-gray-500">
            {workingCount} working {workingCount === 1 ? 'day' : 'days'}
            {remoteCount > 0 && ` · ${remoteCount} remote`}
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Mark the days you typically work, then choose On-Island or Remote for each working day.
        </p>

        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading...</div>
        ) : (
          <>
            <div className="space-y-2 mb-6">
              {schedule.map((entry) => {
                const isChecked = entry.isWorking === true;
                const isOff = entry.isWorking === false;
                const isNotSet = entry.isWorking === null;
                const stateLabel = isChecked
                  ? 'Working'
                  : isOff
                  ? 'Off'
                  : 'Not set';
                const stateClass = isChecked
                  ? 'border-sky-200 bg-sky-50'
                  : isOff
                  ? 'border-gray-200 bg-gray-50'
                  : 'border-dashed border-gray-300 bg-white';
                return (
                  <div
                    key={entry.dayOfWeek}
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border rounded-lg transition-colors ${stateClass}`}
                  >
                    <button
                      type="button"
                      onClick={() => handleToggleStatus(entry.dayOfWeek)}
                      aria-pressed={isChecked}
                      className="flex items-center gap-3 flex-1 text-left"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        readOnly
                        ref={(element) => {
                          if (element) element.indeterminate = isNotSet;
                        }}
                        className="w-5 h-5 text-sky-600 border-gray-300 rounded focus:ring-sky-500 pointer-events-none"
                        tabIndex={-1}
                      />
                      <span className="text-sm font-medium text-gray-900 w-24">
                        {DAY_NAMES[entry.dayOfWeek]}
                      </span>
                      <span
                        className={`text-xs font-medium uppercase tracking-wide ${
                          isChecked
                            ? 'text-sky-700'
                            : isOff
                            ? 'text-gray-500'
                            : 'text-gray-400'
                        }`}
                      >
                        {stateLabel}
                      </span>
                    </button>

                    {isChecked && (
                      <div
                        className="inline-flex p-0.5 rounded-md bg-white border border-gray-200 self-start sm:self-auto"
                        role="group"
                        aria-label={`${DAY_NAMES[entry.dayOfWeek]} location`}
                      >
                        <button
                          type="button"
                          onClick={() => handleLocationChange(entry.dayOfWeek, false)}
                          aria-pressed={entry.isRemote === false}
                          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            entry.isRemote === false
                              ? 'bg-sky-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          On-Island
                        </button>
                        <button
                          type="button"
                          onClick={() => handleLocationChange(entry.dayOfWeek, true)}
                          aria-pressed={entry.isRemote === true}
                          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            entry.isRemote === true
                              ? 'bg-purple-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          Remote
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !isDirty}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MyScheduleCard;
